import { Container, Sprite, type Texture } from 'pixi.js';
import { getFrames, makeSprite } from '@/assets/resolver';
import { PROJECTILE_LOOK as LOOK } from '@/config/projectile-look';

export type ItemKind = 'armor' | 'repair';

interface ItemDrop {
  kind: ItemKind;
  /** Units carried (×2 supply drops have their own two-piece look) */
  amount: number;
  x: number;
  y: number;
  sprite: Sprite;
  bobPhase: number;
  /** Despawn countdown (docs/12 M7) — blinks near the end */
  ttl: number;
}

const PICKUP_RADIUS = 42;
const TTL = 14;
const BLINK_TIME = 4;
const TEX: Record<ItemKind, string> = {
  armor: 'pickup/shield', // shield plate sprite doubles as the armor pickup
  repair: 'pickup/repair',
};
/** ×2 supply drops: two stacked pieces + halo ring + wandering sparkle */
const TEX_X2: Record<ItemKind, string> = {
  armor: 'pickup/shield-x2',
  repair: 'pickup/repair-x2',
};

/**
 * Consumable item drops (docs/03): armor = +1 yellow unit, repair = +25 HP.
 * Dropped by boss damage thresholds / rare drone kills.
 */
export class ItemDropSystem {
  readonly view = new Container();
  private items: ItemDrop[] = [];
  private time = 0;
  /** Sparkle frames of the ×2 looks — resolved once */
  private readonly x2Frames: Record<ItemKind, Texture[]> = {
    armor: getFrames(TEX_X2.armor),
    repair: getFrames(TEX_X2.repair),
  };

  /** Live pickups (the loot cap reads this — docs/03 wave 4). */
  get count(): number {
    return this.items.length;
  }

  spawn(kind: ItemKind, x: number, y: number, amount = 1): void {
    const sprite = makeSprite(amount > 1 ? TEX_X2[kind] : TEX[kind]);
    sprite.position.set(x, y);
    this.view.addChild(sprite);
    this.items.push({ kind, x, y, amount, sprite, bobPhase: this.items.length * 1.1, ttl: TTL });
  }

  /** @returns the picked record (kind + amount) — no new object per pickup */
  tryPickup(px: number, py: number): { readonly kind: ItemKind; readonly amount: number } | null {
    for (let i = 0; i < this.items.length; i++) {
      const d = this.items[i]!;
      if (Math.hypot(px - d.x, py - d.y) < PICKUP_RADIUS) {
        d.sprite.destroy();
        this.items.splice(i, 1);
        return d;
      }
    }
    return null;
  }

  update(dt: number): void {
    this.time += dt;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const d = this.items[i]!;
      d.ttl -= dt;
      if (d.ttl <= 0) {
        d.sprite.destroy();
        this.items.splice(i, 1);
      }
    }
  }

  render(): void {
    for (const d of this.items) {
      d.sprite.y = d.y + Math.sin(this.time * 2.6 + d.bobPhase) * 5;
      d.sprite.scale.set(1 + Math.sin(this.time * 3.4 + d.bobPhase) * 0.05);
      if (d.amount > 1) {
        const frames = this.x2Frames[d.kind];
        d.sprite.texture = frames[Math.floor(this.time * LOOK.sparkleFps + d.bobPhase) % frames.length]!;
      }
      d.sprite.alpha = d.ttl < BLINK_TIME ? (Math.sin(this.time * 14) > 0 ? 1 : 0.25) : 1;
    }
  }

  clear(): void {
    for (const d of this.items) d.sprite.destroy();
    this.items = [];
  }
}
